import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { BRAND } from "@/lib/site-data";

const LINKS = [
  { label: "Destinations", href: "#packages" },
  { label: "Services", href: "#services" },
  { label: "Why Us", href: "#why" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const whatsappHref = `https://wa.me/${BRAND.whatsappNumber}?text=${encodeURIComponent(
    "Hi DreamFly Travels, I'd like a quote."
  )}`;

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#FAFAFA]/85 backdrop-blur-xl border-b border-black/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-2.5 sm:gap-3 group"
        >
          <img
            src={BRAND.logoUrl}
            alt="DreamFly Travels logo"
            className="block size-11 sm:size-12 object-contain shrink-0 drop-shadow-sm"
          />
          <div className="flex flex-col leading-none">
            <span
              className={`font-display text-xl sm:text-[24px] tracking-tight transition-colors ${
                scrolled ? "text-[#0F3D3E]" : "text-white"
              }`}
            >
              DreamFly
            </span>
            <span
              className={`font-sans-ui uppercase text-[9px] sm:text-[10px] tracking-[0.28em] mt-0.5 transition-colors ${
                scrolled ? "text-[#2C7A7B]" : "text-white/80"
              }`}
            >
              Travels
            </span>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-sm font-medium tracking-wide transition-colors ${
                scrolled
                  ? "text-[#0A0A0A]/80 hover:text-[#0F3D3E]"
                  : "text-white/90 hover:text-white"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-cta"
            className="rounded-full bg-[#2C7A7B] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#0F3D3E] transition-colors"
          >
            Get a Quote
          </a>
        </div>

        <button
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
          className={`md:hidden p-2 rounded-lg ${
            scrolled ? "text-[#0F3D3E]" : "text-white"
          }`}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="md:hidden bg-[#FAFAFA] border-t border-black/5"
        >
          <div className="px-5 py-5 flex flex-col gap-4">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className="text-[#0A0A0A]/85 text-base font-medium"
              >
                {l.label}
              </a>
            ))}
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-mobile-whatsapp"
              className="mt-2 rounded-full bg-[#2C7A7B] text-white px-5 py-3 text-center text-sm font-medium"
            >
              Get a Quote on WhatsApp
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
